import Button from "@ingka/button";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import SmallCard from "../../components/Card/Small";
import Focus from "../../components/Focus";
import {
  getSelectedEvent,
  getSelectedStore,
  getSelectedTimeslot,
  getUser,
  useAppContext,
} from "../../contextProviders/AppContext";
import useScrollToTop from "../../hooks/ScrollToTop";
import { Communication, CSSEvent, TimeSlot } from "../../types/model";
import { getEventIdFromSlug } from "../../utils";
import { REGISTERED, WAITING_LIST } from "./enums";
import { SuccessLocationState } from "./types";

type Texts = {
  header: string;
  desc: string[];
};

const Wrapper = styled.div`
  display: grid;
  row-gap: var(--space-200);

  > div:first-child {
    grid-row: 2;
  }

  > div:last-child {
    grid-row: 1;
  }

  ${({ theme }) => `${theme.breakpoints.lg} {
    column-gap: var(--space-400);
    grid-template-columns: 1fr 1fr;
    > div:first-child {
      grid-row: 1;
      grid-column: 2;
    }
  }`}
`;

const Header = styled.h1`
  margin-bottom: var(--space-100);
`;

const StyledButton = styled(Button)`
  margin-top: var(--space-200);
  ${({ theme }) => `${theme.breakpoints.lg} {
    margin-top: var(--space-300);
  }`}
`;

const getWaitingListPosition = (timeSlot: TimeSlot) => {
  return timeSlot.registration?.waitingListPosition || -1;
};

const getThankYouTexts = (
  type: string,
  userHasCommunication: boolean
): Texts => {
  if (type === REGISTERED) {
    return {
      header: "result.thankYou2",
      desc: userHasCommunication ? ["result.infoSentTo"] : [],
    };
  } else if (type === WAITING_LIST) {
    const desc = ["result.confirmationDescForRegisterOnWaitingList"];
    if (userHasCommunication) {
      desc.push("result.infoSentTo");
    }

    return {
      header: "result.confirmationHeaderForRegisterOnWaitingList",
      desc,
    };
  }
  return { header: "", desc: [""] };
};

function getCommunicationType(event: CSSEvent, successType: string) {
  const { communications } = event;
  const typeMap: { [key: string]: string } = {
    REGISTERED: "REGISTRATION_CONFIRMED",
    WAITING_LIST: "WAITING_LIST_REGISTRATION_CONFIRMED",
  };

  const communication = communications.find(
    (c: Communication) => c.notificationType === typeMap[successType]
  );

  return communication?.communicationType || "";
}

const Success = () => {
  const [event, setEvent] = useState<CSSEvent | null>(null);
  const { useSelector } = useAppContext();
  const selectedEvent = useSelector(getSelectedEvent);
  const selectedTimeslot = useSelector(getSelectedTimeslot);
  const selectedStore = useSelector(getSelectedStore);
  const user = useSelector(getUser);
  const history = useHistory();
  const location = useLocation();
  const { eventid, storeName } =
    useParams<{ eventid: string; storeName: string }>();
  const { t } = useTranslation();
  const eventId = getEventIdFromSlug(eventid);

  useScrollToTop();

  useEffect(() => {
    if (!selectedEvent) {
      history.replace(`/${storeName}/${eventId}`);
    }
  }, [selectedEvent, eventId, history, storeName]);

  useEffect(() => {
    if (selectedEvent && selectedTimeslot) {
      setEvent({ ...selectedEvent, timeSlots: [selectedTimeslot] });
    }
  }, [selectedEvent, selectedTimeslot]);

  if (!event) {
    return null;
  }

  const { state: successType } = location.state as SuccessLocationState;
  const communicationType = getCommunicationType(event, successType);
  const texts = getThankYouTexts(
    successType,
    Boolean(user.contact[communicationType])
  );

  const positionNumberOnWaitingList = getWaitingListPosition(selectedTimeslot);

  return (
    <Focus>
      <Wrapper>
        <div>
          <SmallCard event={event} />
        </div>
        <div>
          <Header>
            {t("result.thankyou")}
            <br />
            {t(texts.header)}
          </Header>

          {texts.desc.filter(Boolean).map((text, i) => (
            <p key={i}>
              {t(text, {
                communication: user.contact[communicationType],
                positionNumberOnWaitingList,
              })}
            </p>
          ))}

          <StyledButton
            onClick={() => history.replace(`/${selectedStore.slug}`)}
          >
            {t("result.backToAllEvents")}
          </StyledButton>
        </div>
      </Wrapper>
    </Focus>
  );
};

export default Success;
